import React, {useState, useEffect, useRef} from 'react';
import {Routes, Route, useNavigate} from 'react-router-dom';
import post, {USRINFO_EVENT} from './utils/post.js';
import $ from 'jquery';

import './styles/fonts.scss';
import './styles/index.scss';

import LandingPage from './pages/LandingPage.js';
import Register from './pages/Register.js';
import Forget from './pages/Forget.js';
import Restore from './pages/Restore.js';
import Activate from './pages/Activate.js';
import Login from './pages/Login.js';
import NotFound from './pages/NotFound.js';
import Home from './pages/Home.js';
import Message from './pages/Message.js';
import MessageSysadmin from './pages/MessageSysadmin.js';
import Rule from './pages/Rule.js';
import Pay from './pages/Pay.js';
import MyPayment from './pages/MyPayment.js';
import MyBonus from './pages/MyBonus.js';
import Timer from './pages/Timer.js';
import Links from './pages/Links.js';
import Note from './pages/Note.js';
import Setting from './pages/Setting.js';
import WorkoutMax from './pages/WorkoutMax.js';
import WorkoutOffic from './pages/WorkoutOffic.js';
import Workout from './pages/Workout.js';
import Musclegroup from './pages/Musclegroup.js';
import UserLog from './pages/UserLog.js';
import Stat from './pages/Stat.js';
import Temperature from './pages/Temperature.js';
import EshopSysadmin from './pages/EshopSysadmin.js';
import EshopItem from './pages/EshopItem.js';
import Eshop from './pages/Eshop.js';
import Keys from './pages/Keys.js';
import Lesson from './pages/Lesson.js';
import Payment from './pages/Payment.js';
import Member from './pages/Member.js';
import InstructorHome from './pages/InstructorHome.js';
import InstructorUser from './pages/InstructorUser.js';
import User from './pages/User.js';
import SysadminMonitor from './components/SysadminMonitor.js';

import './styles/summaryFilterTabsOverrides.scss';
import Trans from './pages/Trans.js';
import Exercise from './pages/Exercise.js';
import CalendarEvents from './pages/CalendarEvents.js';

import notificationSound from './audio1.wav';

const audio = new Audio(notificationSound);

const playAnnRef = React.createRef();
const NOTIFICATION_DENIED_REMINDER_KEY = 'notificationDeniedReminderAt';
const NOTIFICATION_DENIED_REMINDER_MS = 7 * 24 * 60 * 60 * 1000;

// Role, podle kterých se skládají routy a ikonky v hlavičce.
const ROLE_KEYS = ['clubMember', 'sysAdmin', 'manager', 'instructor'];

const getNotificationDeniedReminderAt = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	const rawValue = window.localStorage.getItem(NOTIFICATION_DENIED_REMINDER_KEY);
	const parsedValue = Number(rawValue);

	if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
		return null;
	}

	return parsedValue;
};

const setNotificationDeniedReminderAt = (timestamp) => {
	if (typeof window === 'undefined') {
		return;
	}

	window.localStorage.setItem(NOTIFICATION_DENIED_REMINDER_KEY, String(timestamp));
};

const clearNotificationDeniedReminderAt = () => {
	if (typeof window === 'undefined') {
		return;
	}

	window.localStorage.removeItem(NOTIFICATION_DENIED_REMINDER_KEY);
};

const getNotificationPermission = () => {
	if (typeof window === 'undefined' || !('Notification' in window)) {
		return 'unsupported';
	}

	return Notification.permission;
};

const canUsePushNotifications = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

const urlBase64ToUint8Array = (base64String) => {
	const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
	const rawData = window.atob(base64);

	return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const syncPushSubscription = async () => {
	if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
		return null;
	}

	if (Notification.permission !== 'granted') {
		return null;
	}

	const keyRes = await post('getPushPublicKey', {});

	if (!keyRes.success || !keyRes.data) {
		throw new Error(keyRes.error || 'Unable to load VAPID public key');
	}

	const registration = await navigator.serviceWorker.ready;
	let subscription = await registration.pushManager.getSubscription();

	if (!subscription) {
		subscription = await registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: urlBase64ToUint8Array(keyRes.data),
		});
	}

	const subscriptionData = subscription.toJSON ? subscription.toJSON() : subscription;
	const saveRes = await post('savePushSubscription', {subscription: JSON.stringify(subscriptionData)});

	if (!saveRes.success) {
		throw new Error(saveRes.error || 'Unable to save push subscription');
	}

	return subscription;
};

const getExistingPushSubscription = async () => {
	if (!canUsePushNotifications()) {
		return null;
	}

	const registration = await navigator.serviceWorker.ready;
	return registration.pushManager.getSubscription();
};

const showTransactionNotification = async (transCount) => {
	if (typeof window === 'undefined' || !('Notification' in window)) {
		return;
	}

	if (Notification.permission !== 'granted') {
		return;
	}

	const title = 'Nova transakce';
	const body = transCount === 1 ? 'Pribyla 1 nova transakce.' : `Pribyly ${transCount} nove transakce.`;

	if ('serviceWorker' in navigator) {
		try {
			const registration = await navigator.serviceWorker.getRegistration();

			if (registration) {
				registration.showNotification(title, {
					body,
					tag: 'new-transaction',
					renotify: true,
				});
				
				return;
			}
		} catch (error) {
			console.error(error);
		}
	}

	new Notification(title, {
		body,
		tag: 'new-transaction',
	});
};

function App() {
	const [loading, setLoading] = useState(true);
	const [token, setToken] = useState(localStorage.getItem('token') || '');
	const [usrInfo, setUsrInfo] = useState({usrId: 0});
	const [timerCnt, setTimerCnt] = useState({});
	const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
	const [hasPushSubscription, setHasPushSubscription] = useState(null);
	const [notificationDeniedReminderAt, setNotificationDeniedReminderAtState] = useState(getNotificationDeniedReminderAt());
	const supportsPushNotifications = canUsePushNotifications();
	const shouldShowDeniedNotificationPrompt = notificationPermission === 'denied'
		&& (!notificationDeniedReminderAt || (Date.now() - notificationDeniedReminderAt) >= NOTIFICATION_DENIED_REMINDER_MS);
	const shouldShowPermissionRequestPrompt = notificationPermission !== 'denied' && notificationPermission !== 'granted';
	const shouldShowPushSubscriptionPrompt = notificationPermission === 'granted'
		&& supportsPushNotifications
		&& hasPushSubscription === false;
	const shouldShowNotificationPrompt = token
		&& notificationPermission !== 'unsupported'
		&& (
			shouldShowDeniedNotificationPrompt
			|| shouldShowPermissionRequestPrompt
			|| shouldShowPushSubscriptionPrompt
		);

	const timerCntIdRef = useRef(null);
	const isCheckingRef = useRef(false);
	const navigate = useNavigate();
	const usrInfoRef = useRef(usrInfo);
	usrInfoRef.current = usrInfo;
	const roleKey = ROLE_KEYS.map((key) => usrInfo[key]).join('-');

	// Změna role (CLUBMEMBER / MANAGER / INSTRUCTOR / SYSADMIN) se projeví bez zavření
	// appky: post() po každé odpovědi (vč. 10s pollingu getTimerData) pošle aktuální
	// usrInfo, my ho při změně role převezmeme a přejdeme na úvodní stránku – člen
	// na rezervace (Home), nečlen na Výkony (Exercise). Routes mají roleKey jako key,
	// takže se aktuální stránka i s hlavičkou přenačte načisto.
	useEffect(() => {
		const handleUsrInfo = (event) => {
			const freshUsrInfo = event.detail;
			const currentUsrInfo = usrInfoRef.current;

			// Jiný uživatel (bootstrap, přihlášení za jiného uživatele) se řeší vlastním reloadem.
			if (!currentUsrInfo.usrId || freshUsrInfo.usrId !== currentUsrInfo.usrId) {
				return;
			}

			if (!ROLE_KEYS.some((key) => freshUsrInfo[key] !== currentUsrInfo[key])) {
				return;
			}

			usrInfoRef.current = freshUsrInfo;
			setUsrInfo(freshUsrInfo);
			navigate('/', {replace: true});
		};

		window.addEventListener(USRINFO_EVENT, handleUsrInfo);

		return () => {
			window.removeEventListener(USRINFO_EVENT, handleUsrInfo);
		};
	}, [navigate]);

	useEffect(() => {
		bootstrapSession();

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				checkTransCnt();
			}
		};

		document.addEventListener(
			'visibilitychange',
			handleVisibilityChange
		);

		return () => {
			document.removeEventListener(
				'visibilitychange',
				handleVisibilityChange
			);

			if (timerCntIdRef.current) {
				clearTimeout(timerCntIdRef.current);
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const startTimer = () => {
		if (timerCntIdRef.current) {
			clearTimeout(timerCntIdRef.current);
		}

		timerCntIdRef.current = setTimeout(() => {
			checkTransCnt();
		}, 10000);
	};

	const checkTransCnt = () => {
	if (isCheckingRef.current) {
		return;
	}

	// Nepřihlášený uživatel (login / landing page) – getTimerData by přes
	// auth redirect v post() přesměroval na úvodní stránku, proto nic nekontrolujeme.
	if (!localStorage.getItem('token')) {
		return;
	}

	isCheckingRef.current = true;

	post('getTimerData', {})
		.then((res) => {
			let cT = parseInt(res.data.data_trans_cnt) || 0;
			let cE = parseInt(res.data.data_todayeshop_cnt) || 0;
			let cL = parseInt(res.data.data_lesson_cnt) || 0;

			let tV = parseInt($('#trsCnt').val());
			let eV = parseInt($('#espCnt').val());
			let lV = parseInt($('#lesCnt').val());

			if (cT > tV || cE > eV || cL > lV) {
				playAnnRef.current.click();
			}

			if (cT > tV) {
				if (!canUsePushNotifications()) {
					showTransactionNotification(cT - tV);
				}
			}

			$('#trsCnt').val(cT);
			$('#espCnt').val(cE);
			$('#lesCnt').val(cL);

			setTimerCnt(res.data);

			// Ceny se aktualizují automaticky přes tento 10s polling (getUsrInfo
			// vrací vždy aktuální ceny z cache), takže uživatel uvidí nové ceny
			// bez reloadu stránky. Stav měníme jen když se cena reálně změní.
			if (res.usrInfo) {
				setUsrInfo((prev) => {
					const priceKeys = ['PROMO_BONUS', 'ENTRY_PRICE_ONE', 'ENTRY_PRICE_WEEK1', 'ENTRY_PRICE_WEEK2', 'ENTRY_PRICE_MONTH1', 'ENTRY_PRICE_MONTH2', 'ENTRY_PRICE_MONTH3'];
					const changed = priceKeys.some((key) => prev[key] !== res.usrInfo[key]);

					if (!changed) {
						return prev;
					}

					const next = {...prev};
					priceKeys.forEach((key) => { next[key] = res.usrInfo[key]; });
					return next;
				});
			}
		})
		.catch((error) => {
			console.error(error);
		})
		.finally(() => {
			isCheckingRef.current = false;
			startTimer();
		});
	};

	const bootstrapSession = () => {
		setLoading(true);

		// Vždy se serveru zeptáme na session – i bez tokenu v localStorage, protože
		// platná session může přijít z HttpOnly cookie (typicky iOS/Safari, kde byl
		// localStorage vymazán). skipAuthRedirect zabrání redirect smyčce, když
		// uživatel přihlášený není.
		post('getInitData', {}, {skipAuthRedirect: true})
			.then((res) => {
				const usrInfoRes = res && res.usrInfo ? res.usrInfo : {usrId: 0, token: ''};

				if (usrInfoRes.usrId > 0) {
					// Session přišla z cookie → token si znovu uložíme do localStorage,
					// aby zbytek appky (Authorization hlavička) fungoval beze změny.
					if (usrInfoRes.token) {
						localStorage.setItem('token', usrInfoRes.token);
						setToken(usrInfoRes.token);
					}

					setUsrInfo(usrInfoRes);
					checkTransCnt();
				} else {
					localStorage.removeItem('token');
					setToken('');
				}

				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const handleUserMenuClick = (e) => {
		const menuTrigger = $('#iconmenu');

		if (e.target.closest('.usermenu')) {
			return;
		}

		if (e.target.closest('#iconmenu')) {
			if ($('.usermenu').is(':visible')) {
				$('.usermenu').slideUp(100);
				menuTrigger.removeClass('is-active').attr('aria-expanded', 'false');
			} else {
				$('.usermenu').slideDown(100);
				menuTrigger.addClass('is-active').attr('aria-expanded', 'true');
			}
		} else {
			$('.usermenu').slideUp(100);
			menuTrigger.removeClass('is-active').attr('aria-expanded', 'false');
		}
	};

	const playAnn = () => {
		audio.play();
	};

	const handleNotificationPermissionClick = async () => {
		const currentPermission = getNotificationPermission();

		if (currentPermission !== notificationPermission) {
			setNotificationPermission(currentPermission);
		}

		if (currentPermission === 'unsupported') {
			return;
		}

		if (currentPermission === 'denied') {
			const timestamp = Date.now();
			setNotificationDeniedReminderAt(timestamp);
			setNotificationDeniedReminderAtState(timestamp);
			window.alert('Notifikace jsou v prohlizeci blokovane. Pro jejich znovupovoleni zmente opravneni webu v nastaveni prohlizece nebo aplikace.');
			return;
		}

		try {
			let permission = currentPermission;

			if (permission !== 'granted') {
				permission = await Notification.requestPermission();
			}

			setNotificationPermission(permission);

			if (permission === 'denied') {
				const timestamp = Date.now();
				setNotificationDeniedReminderAt(timestamp);
				setNotificationDeniedReminderAtState(timestamp);
				return;
			}

			clearNotificationDeniedReminderAt();
			setNotificationDeniedReminderAtState(null);

			if (permission === 'granted' && supportsPushNotifications) {
				const subscription = await syncPushSubscription();
				setHasPushSubscription(Boolean(subscription));
			}
		} catch (error) {
			console.error(error);
			setNotificationPermission(getNotificationPermission());
			setHasPushSubscription(false);
		}
	};


	useEffect(() => {
		document.addEventListener('click', handleUserMenuClick);

		return () => {
			document.removeEventListener('click', handleUserMenuClick);
		};
	}, []);

	useEffect(() => {
		const syncNotificationPermission = () => {
			setNotificationPermission(getNotificationPermission());
			setNotificationDeniedReminderAtState(getNotificationDeniedReminderAt());
		};

		window.addEventListener('focus', syncNotificationPermission);
		document.addEventListener('visibilitychange', syncNotificationPermission);

		return () => {
			window.removeEventListener('focus', syncNotificationPermission);
			document.removeEventListener('visibilitychange', syncNotificationPermission);
		};
	}, []);

	useEffect(() => {
		if (notificationPermission === 'denied') {
			return;
		}

		clearNotificationDeniedReminderAt();
		setNotificationDeniedReminderAtState(null);
	}, [notificationPermission]);

	useEffect(() => {
		if (!token || notificationPermission !== 'granted' || !supportsPushNotifications) {
			setHasPushSubscription(null);
			return;
		}

		getExistingPushSubscription()
			.then((subscription) => {
				setHasPushSubscription(Boolean(subscription));
			})
			.catch((error) => {
				console.error(error);
				setHasPushSubscription(false);
			});
	}, [notificationPermission, supportsPushNotifications, token]);

	useEffect(() => {
		if (!token || notificationPermission !== 'granted' || !supportsPushNotifications) {
			return;
		}

		syncPushSubscription()
			.then((subscription) => {
				setHasPushSubscription(Boolean(subscription));
			})
			.catch((error) => {
				console.error(error);
				setHasPushSubscription(null);
			});
	}, [notificationPermission, supportsPushNotifications, token]);

	if (loading) {
		return <div className="loading" />;
	}

	return (
		<div className="app" id="app">
			{loading ? <div className="loading" /> : ''}

			<button id="playAnnID" ref={playAnnRef} onClick={() => playAnn()} className="hidden"></button>
			<input id="trsCnt" type="hidden" />
			<input id="espCnt" type="hidden" />
			<input id="lesCnt" type="hidden" />
			
			{shouldShowNotificationPrompt ? (
				<button
					type="button"
					className="icon notification_off notification_prompt"
					onClick={handleNotificationPermissionClick}
					title="Povolit notifikace"
					aria-label="Povolit notifikace"
				/>
			) : ('')}

			{token ? (
				<>
					<Routes key={roleKey}>
						{usrInfo.clubMember === 1 ? (
							<>
								<Route exact path="/" element={<Home usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/home" element={<Home usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/login" element={<Home usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/eshop" element={<Eshop usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/pay" element={<Pay usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/mypayment" element={<MyPayment usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/mybonus" element={<MyBonus usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/message" element={<Message usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/rule" element={<Rule usrInfo={usrInfo} timerCnt={timerCnt} />} />
							</>
						) : (
							<>
								<Route exact path="/" element={<Exercise usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/home" element={<Exercise usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/login" element={<Exercise usrInfo={usrInfo} timerCnt={timerCnt} />} />
							</>
						)}

						<Route exact path="/calendarevents" element={<CalendarEvents usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/lesson" element={<Lesson usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/exercise" element={<Exercise usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/exercise/:targetUsrId" element={<Exercise usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/workoutmax" element={<WorkoutMax usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/timer" element={<Timer usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/workout" element={<Workout usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/workout/:targetUsrId" element={<Workout usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/musclegroup" element={<Musclegroup usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/musclegroup/:targetUsrId" element={<Musclegroup usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/stat" element={<Stat usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/temperature" element={<Temperature usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/links" element={<Links usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/note" element={<Note usrInfo={usrInfo} timerCnt={timerCnt} />} />
						<Route exact path="/setting" element={<Setting usrInfo={usrInfo} timerCnt={timerCnt} setUsrInfo={setUsrInfo} />} />

						{usrInfo.sysAdmin === 1 || usrInfo.manager === 1 ? (
							<>
								<Route exact path="/member" element={<Member usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/member/:directSearch" element={<Member usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/trans" element={<Trans usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/payment" element={<Payment usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/payment/:directSearch" element={<Payment usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/eshopsysadmin" element={<EshopSysadmin usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/eshopsysadmin/:directSearch" element={<EshopSysadmin usrInfo={usrInfo} timerCnt={timerCnt} />} />
							</>
						) : (
							<></>
						)}

						{usrInfo.sysAdmin === 1 ? (
							<>
								<Route exact path="/user" element={<User usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/user/:directSearch" element={<User usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/keys" element={<Keys usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/messagesysadmin" element={<MessageSysadmin usrInfo={usrInfo} timerCnt={timerCnt} />} />
								{/* Sysadmin si stránku pozvánek a bonusů otevře i pro vybraného uživatele (ikonka v monitoringu). */}
								<Route exact path="/mybonus/:targetUsrId" element={<MyBonus usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/userlog" element={<UserLog usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/userlog/:directSearch" element={<UserLog usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/eshopitem" element={<EshopItem usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/workoutoffic" element={<WorkoutOffic usrInfo={usrInfo} timerCnt={timerCnt} />} />
							</>
						) : (
							<></>
						)}

						{usrInfo.instructor === 1 ? (
							<>
								<Route exact path="/instructorhome" element={<InstructorHome usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/instructorusr" element={<InstructorUser usrInfo={usrInfo} timerCnt={timerCnt} />} />
								<Route exact path="/instructorusr/:directSearch" element={<InstructorUser usrInfo={usrInfo} timerCnt={timerCnt} />} />
							</>
						) : (
							<></>
						)}

						<Route exact path="*" element={<NotFound />} />
					</Routes>

					{usrInfo.sysAdmin === 1 ? <SysadminMonitor usrInfo={usrInfo} /> : ''}
				</>
			) : (
				<>
					<Routes>
						<Route exact path="/" element={<LandingPage />} />
						<Route exact path="/login" element={<Login />} />
						<Route exact path="/forget" element={<Forget />} />
						<Route exact path="/forgetr" element={<Restore />} />
						<Route exact path="/register" element={<Register />} />
						<Route exact path="/activate" element={<Activate />} />
						<Route exact path="*" element={<NotFound />} />
					</Routes>
				</>
			)}
		</div>
	);
}

export default App;
